import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import {
  DraggableContainerComponent,
  QaiButtonComponent,
  QaiIconComponent,
  QaiSimulationCardComponent,
  QaiLoadingComponent,
  DraggableItem,
} from '@qai/ui';
import { finalize, Subscription, take } from 'rxjs';
import { TabsInnerWrapComponent } from '../../components/tabs-inner-wrap/tabs-inner-wrap.component';
import { ActivatedRoute } from '@angular/router';
import { AddSimulationModalComponent } from '../../components/modals/add-simulation-to-assignment/add-simulation-to-assignment.component';
import { ISimulationByModuleAssignment, ISimulationsInModuleAssignment } from 'src/app/interfaces/self-serve/assignment-manager/responses/isimulation-by-module-assignment';
import { AssignmentsService } from 'src/app/services/self-serve/assignments.service';
import { ISimulationsInAssignment } from 'src/app/interfaces/self-serve/assignment-manager/isimulation-in-assignment';
import { ISimulationPayload, IUpdateSimulationsLink } from 'src/app/interfaces/self-serve/assignment-manager/requests/isimulation-in-assignment';
import { AssignmentReactiveFormService } from '../../services/assignment-reactive-form.service';
import { FormGroup } from '@angular/forms';
import { AssignmentTypeEnum } from 'src/app/utils/enums/self-serve/assignment-type.enum';
import { QuestionStrategyEnum } from 'src/app/utils/enums/self-serve/question-strategy.enum';

interface RandomizationFormState {
  enabled: boolean;
  questionStrategy: QuestionStrategyEnum | null;
  randomQuestionPool: string;
  randomQuestionCount: number | null;
  poolError: string | null;
  countError: string | null;
}

@Component({
  selector: 'tab-simulation-details',
  standalone: true,
  templateUrl: './simulations-content.component.html',
  styleUrls: ['./simulations-content.component.scss'],
  imports: [
    DraggableContainerComponent,
    QaiSimulationCardComponent,
    QaiButtonComponent,
    QaiIconComponent,
    TabsInnerWrapComponent,
    AddSimulationModalComponent,
    QaiLoadingComponent,
  ],
})
export class SimulationDetailsComponent implements OnInit, OnDestroy {
  readonly isLoading = signal(true);
  readonly simulations = signal<ISimulationsInAssignment[]>([]);
  readonly addSimulationsToAssignmentModalOpen = signal(false);
  readonly isCompleted = signal(false);
  readonly showAttemptsAllowed = signal(true);
  readonly randomizationState = signal<Record<string, RandomizationFormState>>({});

  moduleAssignmentUuid = '';
  moduleUuid = '';
  selectedCard: ISimulationsInModuleAssignment = {} as ISimulationsInModuleAssignment;

  private assignmentTypeSubscription?: Subscription;

  protected readonly labels = {
    noSimulations: 'No simulations found',
    attemptsAllowed: 'Attempts allowed',
    randomizedQuestions: 'Randomized questions',
    questionsPerUser: 'Questions per user',
    randomizationPool: 'Randomization pool',
    attemptsSubLabel: 'Number of attempts per user',
    countSubLabel: 'Number of questions each user will receive',
    poolPlaceholder: 'Enter question UUIDs',
    selectPlaceholder: 'Select',
    addSimulation: 'Add simulation to assignment',
    removeSimulationTitle: 'Remove simulation from assignment',
    invalidPoolError: 'One or more question IDs are invalid',
    countPositiveError: 'Question count must be a positive integer.',
    countExceedsPoolError: 'Exceeds available question pool',
    attemptsUnlimited: 'Unlimited',
    strategyNo: 'No',
    strategyYesPerUser: 'Yes, per user',
    strategyYesPerSession: 'Yes, per session',
  } as const;

  readonly attemptsOptions = [
    { value: 'unlimited', label: this.labels.attemptsUnlimited },
    ...Array.from({ length: 10 }, (_, i) => ({ value: String(i + 1), label: String(i + 1) })),
  ];
  readonly strategyOptions = [
    { value: 'NO', label: this.labels.strategyNo },
    { value: QuestionStrategyEnum.RANDOM_FIXED, label: this.labels.strategyYesPerUser },
    { value: QuestionStrategyEnum.RANDOM_EVERY_TIME, label: this.labels.strategyYesPerSession },
  ];
  readonly countSelectOptions = Array.from({ length: 20 }, (_, i) => ({
    value: String(i + 1), label: String(i + 1),
  }));

  protected readonly randomizationLabels = {
    randomizedQuestions: this.labels.randomizedQuestions,
    questionsPerUser: this.labels.questionsPerUser,
    countSubLabel: this.labels.countSubLabel,
    randomizationPool: this.labels.randomizationPool,
    poolPlaceholder: this.labels.poolPlaceholder,
  };

  constructor(
    private assignmentsService: AssignmentsService,
    private assignmentReactiveFormService: AssignmentReactiveFormService,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.moduleAssignmentUuid = this.route.snapshot.paramMap.get('uuid')!;
    this.isCompleted.set(this.assignmentReactiveFormService.isCompleted(this.moduleAssignmentUuid));
    this.loadModuleUuid();
    this.fetchSimulations();
    this.subscribeToAssignmentTypeChanges();
  }

  ngOnDestroy(): void {
    this.assignmentTypeSubscription?.unsubscribe();
  }

  private subscribeToAssignmentTypeChanges(): void {
    this.assignmentReactiveFormService.getForm(this.moduleAssignmentUuid).pipe(take(1)).subscribe((form) => {
      const detailsGroup = form.get('details') as FormGroup;
      const assignmentTypeControl = detailsGroup?.get('moduleAssignmentType');

      if (assignmentTypeControl) {
        this.updateShowAttemptsAllowed(assignmentTypeControl.value);

        this.assignmentTypeSubscription?.unsubscribe();
        this.assignmentTypeSubscription = assignmentTypeControl.valueChanges.subscribe((assignmentType: AssignmentTypeEnum) => {
          this.updateShowAttemptsAllowed(assignmentType);
        });
      }
    });
  }

  private updateShowAttemptsAllowed(assignmentType: AssignmentTypeEnum): void {
    this.showAttemptsAllowed.set(assignmentType !== AssignmentTypeEnum.PERSONALIZED_PRACTICE);
  }

  private fetchSimulations(): void {
    if (!this.moduleAssignmentUuid) {
      console.warn('No moduleAssignmentUuid provided.');
      this.simulations.set([]);
      this.isLoading.set(false);
      return;
    }

    this.assignmentsService
      .getSimulationsByModuleAssignmentUuid(this.moduleAssignmentUuid)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe((res: ISimulationByModuleAssignment) => {
        if (res?.simulations) {
          this.simulations.set(
            res.simulations.map(sim => ({
              ...sim,
              numberOfAttemptsAllowed: sim.numberOfAttemptsAllowed === null ? 'unlimited' : sim.numberOfAttemptsAllowed,
            }))
          );
          this.showAttemptsAllowed.set(res.moduleAssignmentType !== AssignmentTypeEnum.PERSONALIZED_PRACTICE);

          const initialState: Record<string, RandomizationFormState> = {};
          res.simulations.forEach(sim => {
            const isEnabled = !!sim.questionStrategy && sim.questionStrategy !== QuestionStrategyEnum.STANDARD;
            initialState[sim.callUuid] = {
              enabled: isEnabled,
              questionStrategy: isEnabled ? (sim.questionStrategy as QuestionStrategyEnum) : null,
              randomQuestionPool: sim.randomQuestionPool ?? '',
              randomQuestionCount: sim.randomQuestionCount ?? null,
              poolError: null,
              countError: null,
            };
          });
          this.randomizationState.set(initialState);
        } else {
          this.simulations.set([]);
        }

        this.assignmentReactiveFormService.getForm(this.moduleAssignmentUuid).pipe(take(1)).subscribe((form) => {
          const detailsGroup = form.get('details') as FormGroup;
          if (detailsGroup.get('simulationsCount')) {
            this.assignmentReactiveFormService.patchForm(this.moduleAssignmentUuid, 'details', {
              simulationsCount: this.simulations().length,
            });
          }
        });
      });
  }

  onSimulationsChange(updatedSimulations: DraggableItem[]): void {
    if (!this.moduleAssignmentUuid) {
      console.warn('moduleAssignmentUuid not set. Skipping API call.');
      return;
    }

    const simulationsPayload: ISimulationPayload[] = updatedSimulations.map((sim, index) => {
      const item = sim as ISimulationsInAssignment;
      return {
        callUuid: item.callUuid,
        numberOfAttemptsAllowed: typeof item.numberOfAttemptsAllowed === 'number' ? item.numberOfAttemptsAllowed : null,
        callOrder: index + 1,
      };
    });

    const payload: IUpdateSimulationsLink = {
      moduleAssignmentUuid: this.moduleAssignmentUuid,
      updateNumberOfAttemptsAllowed: false,
      simulations: simulationsPayload,
    };

    this.assignmentsService.updateSimulationsLink(payload).subscribe({
      error: (err) => console.error('Error updating simulations order:', err),
    });
  }

  removeLinkClick(item: ISimulationsInAssignment): void {
    if (!this.moduleAssignmentUuid) {
      console.warn('moduleAssignmentUuid not set. Skipping API call.');
      return;
    }

    this.assignmentsService
      .removeSimulationFromAssignment(this.moduleAssignmentUuid, item.callUuid)
      .subscribe({
        next: () => this.fetchSimulations(),
        error: (err) => console.error('Failed to remove simulation:', err),
      });
  }

  setSelectedCard(item: ISimulationsInModuleAssignment): void {
    this.selectedCard = item;
  }

  onAttemptsSelected(value: number | string): void {
    if (!this.selectedCard) return;

    const updatedCard: ISimulationsInModuleAssignment = {
      ...this.selectedCard,
      numberOfAttemptsAllowed: typeof value === 'number' ? value : null,
    };

    const payload: IUpdateSimulationsLink = {
      moduleAssignmentUuid: this.moduleAssignmentUuid,
      updateNumberOfAttemptsAllowed: true,
      simulations: [updatedCard],
    };

    this.assignmentsService.updateSimulationsLink(payload).subscribe({
      next: () => {
        this.simulations.update(sims =>
          sims.map(sim =>
            sim.callUuid === updatedCard.callUuid
              ? { ...sim, numberOfAttemptsAllowed: updatedCard.numberOfAttemptsAllowed === null ? 'unlimited' : updatedCard.numberOfAttemptsAllowed }
              : sim
          )
        );
        this.selectedCard = {} as ISimulationsInModuleAssignment;
      },
      error: (err) => console.error('Error updating simulation attempts', err),
    });
  }

  openAddSimulationModal(): void {
    this.loadModuleUuid();
    this.addSimulationsToAssignmentModalOpen.set(true);
  }

  private loadModuleUuid(): void {
    this.assignmentReactiveFormService
      .getForm(this.moduleAssignmentUuid)
      .pipe(take(1))
      .subscribe((form) => {
        const detailsGroup = form.get('details') as FormGroup;
        this.moduleUuid = detailsGroup?.get('moduleUuid')?.value || '';
      });
  }

  closeAssignmentModal(): void {
    this.addSimulationsToAssignmentModalOpen.set(false);
  }

  closeModalAndReloadSimulations(): void {
    this.closeAssignmentModal();
    this.fetchSimulations();
  }

  // --- Randomization helpers ---

  getRandomState(callUuid: string): RandomizationFormState {
    return this.randomizationState()[callUuid] ?? {
      enabled: false,
      questionStrategy: null,
      randomQuestionPool: '',
      randomQuestionCount: null,
      poolError: null,
      countError: null,
    };
  }

  onStrategyChange(item: ISimulationsInModuleAssignment, value: string): void {
    if (value === 'NO') {
      this.randomizationState.update(s => ({
        ...s,
        [item.callUuid]: {
          enabled: false,
          questionStrategy: null,
          randomQuestionPool: '',
          randomQuestionCount: null,
          poolError: null,
          countError: null,
        },
      }));
      this.saveRandomizationStrategy(item, QuestionStrategyEnum.STANDARD, null, null);
    } else {
      const strategy = value as QuestionStrategyEnum;
      const currentState = this.randomizationState()[item.callUuid];

      if (!currentState?.enabled) {
        this.randomizationState.update(s => ({
          ...s,
          [item.callUuid]: {
            enabled: true,
            questionStrategy: strategy,
            randomQuestionPool: '',
            randomQuestionCount: null,
            poolError: null,
            countError: null,
          },
        }));
      } else {
        this.updateRandomState(item.callUuid, { questionStrategy: strategy });
      }

      const newState = this.randomizationState()[item.callUuid];
      this.saveRandomizationStrategy(item, strategy, newState.randomQuestionPool || null, newState.randomQuestionCount);
    }
  }

  onPoolBlur(item: ISimulationsInModuleAssignment, event: Event): void {
    const value = (event.target as HTMLInputElement).value.trim();
    this.updateRandomState(item.callUuid, { randomQuestionPool: value });

    if (!value) {
      this.updateRandomState(item.callUuid, { poolError: this.labels.invalidPoolError });
      return;
    }

    const uuids = value.split(',').map(u => u.trim()).filter(u => !!u);
    this.assignmentsService.validateQuestionUuids(item.callUuid, uuids).subscribe({
      next: (res) => {
        if (res.valid) {
          this.updateRandomState(item.callUuid, { poolError: null });
          const state = this.randomizationState()[item.callUuid];
          if (state.randomQuestionCount !== null) {
            this.validateCount(item.callUuid, uuids.length);
          }
          const updatedState = this.randomizationState()[item.callUuid];
          if (!updatedState.countError) {
            this.saveRandomizationStrategy(item, updatedState.questionStrategy ?? QuestionStrategyEnum.STANDARD, value, updatedState.randomQuestionCount);
          }
        } else {
          this.updateRandomState(item.callUuid, { poolError: this.labels.invalidPoolError });
        }
      },
      error: () => {
        this.updateRandomState(item.callUuid, { poolError: this.labels.invalidPoolError });
      },
    });
  }

  onCountChange(item: ISimulationsInModuleAssignment, raw: string): void {
    const count = raw ? parseInt(raw, 10) : null;
    this.updateRandomState(item.callUuid, { randomQuestionCount: count });

    if (count === null || isNaN(count) || count < 1) {
      this.updateRandomState(item.callUuid, { countError: this.labels.countPositiveError });
      return;
    }

    const state = this.randomizationState()[item.callUuid];
    if (state.poolError) {
      this.updateRandomState(item.callUuid, { countError: null });
      return;
    }

    const poolUuids = state.randomQuestionPool ? state.randomQuestionPool.split(',').filter(u => !!u.trim()) : [];
    this.validateCount(item.callUuid, poolUuids.length);

    const updatedState = this.randomizationState()[item.callUuid];
    if (!updatedState.countError) {
      this.saveRandomizationStrategy(item, updatedState.questionStrategy ?? QuestionStrategyEnum.STANDARD, updatedState.randomQuestionPool || null, count);
    }
  }

  private updateRandomState(callUuid: string, updates: Partial<RandomizationFormState>): void {
    this.randomizationState.update(s => ({
      ...s,
      [callUuid]: { ...s[callUuid], ...updates },
    }));
  }

  private validateCount(callUuid: string, poolSize: number): void {
    const state = this.randomizationState()[callUuid];
    const countError =
      state.randomQuestionCount !== null && poolSize > 0 && state.randomQuestionCount > poolSize
        ? this.labels.countExceedsPoolError
        : null;
    this.updateRandomState(callUuid, { countError });
  }

  private saveRandomizationStrategy(
    item: ISimulationsInModuleAssignment,
    questionStrategy: QuestionStrategyEnum,
    randomQuestionPool: string | null,
    randomQuestionCount: number | null,
  ): void {
    if (!this.moduleAssignmentUuid) return;

    const payload: IUpdateSimulationsLink = {
      moduleAssignmentUuid: this.moduleAssignmentUuid,
      updateNumberOfAttemptsAllowed: false,
      updateQuestionStrategy: true,
      simulations: [{
        callUuid: item.callUuid,
        callOrder: (item.callOrder as number) ?? 0,
        numberOfAttemptsAllowed: typeof item.numberOfAttemptsAllowed === 'number' ? item.numberOfAttemptsAllowed : null,
        questionStrategy,
        randomQuestionPool,
        randomQuestionCount,
      }],
    };

    this.assignmentsService.updateSimulationsLink(payload).subscribe({
      error: (err) => console.error('Error updating randomization strategy', err),
    });
  }
}
